// soccer_admin/src/pages/SendNotification.jsx
import React, { useState, useEffect } from 'react';
import { adminSendNotification, adminDeleteNotification } from '../services/api';
import API from '../services/api';
import { 
  BellIcon, UsersIcon, GlobeAltIcon, UserIcon, 
  EnvelopeIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const SendNotification = () => {
  const [targetType, setTargetType] = useState('all');
  const [continent, setContinent] = useState('Europe');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  
  // State for notifications list
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch all notifications (admin)
  const fetchNotifications = async () => {
    setLoadingNotifs(true);
    try {
      const res = await API.get('/notifications/admin/all');
      setNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoadingNotifs(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !message) {
      setResult({ error: 'Title and message are required' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const payload = { title, message };
      if (targetType === 'all') payload.all = true;
      else if (targetType === 'continent') payload.continent = continent;
      else if (targetType === 'user') payload.userId = parseInt(userId);
      
      const res = await adminSendNotification(payload);
      setResult({ success: `✅ Sent to ${res.data.count} user(s)` });
      setTitle('');
      setMessage('');
      // Refresh the list after sending
      await fetchNotifications();
    } catch (err) {
      setResult({ error: err.response?.data?.error || 'Failed to send notification' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (notifId) => {
    if (!window.confirm('Delete this notification? This action cannot be undone.')) return;
    setDeletingId(notifId);
    try {
      await adminDeleteNotification(notifId);
      await fetchNotifications();
    } catch (err) {
      alert('Failed to delete notification');
    } finally {
      setDeletingId(null);
    }
  };

  // Helper to format target audience
  const formatTarget = (notif) => {
    if (notif.target_type === 'all') return '🌍 All Users';
    if (notif.target_type === 'continent') return `🌎 ${notif.target_value}`;
    if (notif.target_type === 'user') return `👤 User #${notif.target_value}`;
    return '—';
  };

  const getReadPercentage = (notif) => {
    if (!notif.total_recipients) return 0;
    return Math.round(((notif.read_count || 0) / notif.total_recipients) * 100);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <BellIcon className="h-8 w-8 text-yellow-400" />
          Send Notification
        </h1>
        <p className="text-gray-400 mt-2">Broadcast messages to players via in-game notifications</p>
      </div>

      {/* Send Form Card */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-xl overflow-hidden mb-8">
        <div className="border-b border-gray-700 px-6 py-4 bg-gray-800/30">
          <div className="flex items-center gap-2">
            <PaperAirplaneIcon className="h-5 w-5 text-yellow-400" />
            <span className="text-yellow-400 font-medium">New Broadcast</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Target Audience</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setTargetType('all')}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all duration-200 ${
                  targetType === 'all'
                    ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300 shadow-md shadow-yellow-500/10'
                    : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                }`}
              >
                <UsersIcon className="h-5 w-5" />
                <span className="font-medium">All Users</span>
              </button>
              <button
                type="button"
                onClick={() => setTargetType('continent')}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all duration-200 ${
                  targetType === 'continent'
                    ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300 shadow-md shadow-yellow-500/10'
                    : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                }`}
              >
                <GlobeAltIcon className="h-5 w-5" />
                <span className="font-medium">By Continent</span>
              </button>
              <button
                type="button"
                onClick={() => setTargetType('user')}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all duration-200 ${
                  targetType === 'user'
                    ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300 shadow-md shadow-yellow-500/10'
                    : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                }`}
              >
                <UserIcon className="h-5 w-5" />
                <span className="font-medium">Specific User</span>
              </button>
            </div>
          </div>

          {/* Continent Selector */}
          {targetType === 'continent' && (
            <div className="animate-fadeIn">
              <label className="block text-sm font-medium text-gray-300 mb-2">Continent</label>
              <div className="relative">
                <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={continent}
                  onChange={(e) => setContinent(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition"
                >
                  <option value="Europe">🇪🇺 Europe</option>
                  <option value="Asia">🇦🇸 Asia</option>
                  <option value="Africa">🇦🇫 Africa</option>
                  <option value="Americas">🌎 Americas</option>
                </select>
              </div>
            </div>
          )}

          {/* User ID Input */}
          {targetType === 'user' && (
            <div className="animate-fadeIn">
              <label className="block text-sm font-medium text-gray-300 mb-2">User ID</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter user ID (e.g., 123)"
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition"
                  required
                />
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Notification Title</label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Special Event: Double XP Weekend!"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition"
                required
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Message Body</label>
            <div className="relative">
              <ChatBubbleLeftRightIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows="4"
                placeholder="Write your message here..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition resize-none"
                required
              />
            </div>
          </div>

          {/* Result Message */}
          {result && (
            <div className={`p-3 rounded-xl flex items-center gap-2 ${
              result.error ? 'bg-red-500/10 border border-red-500/30 text-red-300' : 'bg-green-500/10 border border-green-500/30 text-green-300'
            }`}>
              {result.error ? '⚠️' : '🎉'}
              <span>{result.error || result.success}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={sending}
              className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/20"
            >
              {sending ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-5 w-5" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ========== DESKTOP TABLE (hidden on mobile) ========== */}
      <div className="hidden md:block bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
        <div className="border-b border-gray-700 px-6 py-4 bg-gray-800/30">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-yellow-400" />
            <span className="text-yellow-400 font-medium">Sent Notifications History</span>
          </div>
        </div>
        <div className="p-6">
          {loadingNotifs ? (
            <div className="text-center py-8 text-gray-400">Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No notifications sent yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-700">
                  <tr className="text-gray-400">
                    <th className="pb-3 pr-4">ID</th>
                    <th className="pb-3 pr-4">Target</th>
                    <th className="pb-3 pr-4">Title</th>
                    <th className="pb-3 pr-4">Message</th>
                    <th className="pb-3 pr-4">Recipients</th>
                    <th className="pb-3 pr-4">Read</th>
                    <th className="pb-3 pr-4">Sent At</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {notifications.map((notif) => (
                    <tr key={notif.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-3 pr-4 text-white">{notif.id}</td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1 text-xs bg-gray-700/50 px-2 py-1 rounded-full">
                          {formatTarget(notif)}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-white max-w-[180px] truncate" title={notif.title}>{notif.title}</td>
                      <td className="py-3 pr-4 text-gray-300 max-w-[240px] truncate" title={notif.message}>{notif.message}</td>
                      <td className="py-3 pr-4">
                        <span className="text-yellow-300 font-mono">{notif.total_recipients || 0}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-1">
                          <span className="text-green-400">{notif.read_count || 0}</span>
                          <span className="text-gray-500">/ {notif.total_recipients || 0}</span>
                          {notif.total_recipients > 0 && (
                            <span className="text-xs text-gray-400">
                              ({getReadPercentage(notif)}%)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-400 text-xs whitespace-nowrap">
                        {new Date(notif.created_at).toLocaleString()}
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => handleDelete(notif.id)}
                          disabled={deletingId === notif.id}
                          className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                          title="Delete notification"
                        >
                          {deletingId === notif.id ? (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                          ) : (
                            <TrashIcon className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ========== MOBILE CARDS (visible only on small screens) ========== */}
      <div className="md:hidden space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <BellIcon className="h-5 w-5 text-yellow-400" />
          <span className="text-yellow-400 font-medium">Sent Notifications</span>
        </div>
        {loadingNotifs ? (
          <div className="text-center py-8 text-gray-400">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No notifications sent yet.</div>
        ) : (
          notifications.map((notif) => (
            <div key={notif.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-md hover:shadow-yellow-500/10 transition-all">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="inline-flex items-center gap-1 text-xs bg-gray-700/50 px-2 py-1 rounded-full">
                    {formatTarget(notif)}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(notif.id)}
                  disabled={deletingId === notif.id}
                  className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                >
                  {deletingId === notif.id ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                  ) : (
                    <TrashIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <h3 className="text-white font-bold text-base mb-1">{notif.title}</h3>
              <p className="text-gray-300 text-sm mb-3 line-clamp-2">{notif.message}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-400">ID:</span> <span className="text-white">{notif.id}</span></div>
                <div><span className="text-gray-400">Recipients:</span> <span className="text-yellow-300 font-mono">{notif.total_recipients || 0}</span></div>
                <div><span className="text-gray-400">Read:</span> <span className="text-green-400">{notif.read_count || 0} / {notif.total_recipients || 0}</span></div>
                <div className="col-span-2 mt-1">
                  <div className="flex justify-between text-gray-400 text-[10px] mb-0.5">
                    <span>Read rate</span>
                    <span>{getReadPercentage(notif)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${getReadPercentage(notif)}%` }}></div>
                  </div>
                </div>
                <div className="col-span-2 mt-1 text-gray-400 text-xs">
                  {new Date(notif.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SendNotification;